/**
 * campaignService.js
 * All API calls for the Email Campaign Management module.
 */

import { RBAC_ENDPOINTS } from '../config/api';
import { rbacFetch } from '../utils/apiClient';

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function parseResponse(res) {
  let json = null;
  try {
    json = await res.json();
  } catch {
    // Proxy/gateway errors can come back as HTML — fall through to the status code
  }
  if (!res.ok) {
    throw new Error(json?.message || `Request failed (${res.status})`);
  }
  return json?.data;
}

// ─── Templates ────────────────────────────────────────────────────────────────

export async function fetchTemplateTypes() {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignTemplates);
  return parseResponse(res);
}

export async function fetchTemplate(type) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignTemplate(type));
  return parseResponse(res);
}

// ─── Campaign CRUD ────────────────────────────────────────────────────────────

export async function createCampaign(data) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaigns, {
    method: 'POST',
    body:   JSON.stringify(data),
  });
  return parseResponse(res);
}

export async function fetchCampaigns({ page = 1, limit = 20, status, type, search } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (status) params.set('status', status);
  if (type)   params.set('type', type);
  if (search) params.set('search', search);
  const res = await rbacFetch(`${RBAC_ENDPOINTS.campaigns}?${params}`);
  return parseResponse(res);
}

export async function fetchCampaignDetail(id) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignDetail(id));
  return parseResponse(res);
}

export async function updateCampaign(id, data) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignDetail(id), {
    method: 'PUT',
    body:   JSON.stringify(data),
  });
  return parseResponse(res);
}

export async function deleteCampaign(id) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignDetail(id), {
    method: 'DELETE',
  });
  return parseResponse(res);
}

// ─── Recipients ───────────────────────────────────────────────────────────────

// Recipients go up in chunks so a large selection never hits the request size
// limit. The backend skips anyone already on the campaign, so a chunk that is
// retried after a network error cannot queue the same person twice.
const RECIPIENT_CHUNK_SIZE = 200;

export async function addRecipients(campaignId, recipients) {
  const list = recipients || [];
  let totalInserted = 0;

  for (let i = 0; i < list.length; i += RECIPIENT_CHUNK_SIZE) {
    const chunk = list.slice(i, i + RECIPIENT_CHUNK_SIZE);
    const res = await rbacFetch(RBAC_ENDPOINTS.campaignRecipients(campaignId), {
      method: 'POST',
      body:   JSON.stringify({ recipients: chunk }),
    });
    const data = await parseResponse(res);
    totalInserted += data?.totalInserted || 0;
  }

  return { totalInserted };
}

export async function fetchRecipients(campaignId, { page = 1, limit = 50, status, search } = {}) {
  const params = new URLSearchParams({ page, limit });
  if (status) params.set('status', status);
  if (search) params.set('search', search);
  const res = await rbacFetch(`${RBAC_ENDPOINTS.campaignRecipients(campaignId)}?${params}`);
  return parseResponse(res);
}

export function getRecipientsExportUrl(campaignId, status) {
  let url = RBAC_ENDPOINTS.campaignRecipientsExport(campaignId);
  if (status) url += `?status=${status}`;
  return url;
}

// ─── Send Operations ──────────────────────────────────────────────────────────

export async function sendTestEmail(campaignId, testEmails) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignTestSend(campaignId), {
    method: 'POST',
    body:   JSON.stringify({ testEmails }),
  });
  return parseResponse(res);
}

export async function confirmCampaign(campaignId) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignConfirm(campaignId), {
    method: 'POST',
  });
  return parseResponse(res);
}

export async function startSending(campaignId) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignSend(campaignId), {
    method: 'POST',
  });
  return parseResponse(res);
}

export async function pauseCampaign(campaignId) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignPause(campaignId), {
    method: 'POST',
  });
  return parseResponse(res);
}

export async function retryCampaign(campaignId) {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignRetry(campaignId), {
    method: 'POST',
  });
  return parseResponse(res);
}

// ─── System ───────────────────────────────────────────────────────────────────

export async function fetchSystemStatus() {
  const res = await rbacFetch(RBAC_ENDPOINTS.campaignSystemStatus);
  return parseResponse(res);
}

// ─── Users (for recipient selection) ──────────────────────────────────────────

export async function fetchUsersForRecipients({ role, search } = {}) {
  const params = new URLSearchParams({ page: 1, limit: 500 });
  if (role)   params.set('role', role);
  if (search) params.set('search', search);
  const res = await rbacFetch(`${RBAC_ENDPOINTS.users}?${params}`);
  return parseResponse(res);
}

export async function fetchNotOnboardedUsers() {
  const res = await rbacFetch(`${RBAC_ENDPOINTS.campaigns}/users/not-onboarded`);
  return parseResponse(res);
}
